import { Icon, IconButton, Stack, Text, Upload } from "@assetwatch/ui";
import { WORK_REQUEST_ATTACHMENT_TYPES } from "../../../queries/useWorkRequestFiles";
import type { StagedAttachment } from "./attachmentSelection";
import { RequestFieldLabel } from "./RequestFieldLabel";

type NewRequestAttachmentsProps = {
  files: StagedAttachment[];
  /** One message per file that was not staged. */
  errors: string[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
};

/** Attachments staged on a new request; they upload once it is created. */
export function NewRequestAttachments({
  errors,
  files,
  onAdd,
  onRemove,
}: NewRequestAttachmentsProps) {
  return (
    <Stack gap={4}>
      <RequestFieldLabel>Attachments</RequestFieldLabel>
      <Stack gap={8}>
        <Upload
          accept={WORK_REQUEST_ATTACHMENT_TYPES.join(",")}
          acceptedFormatsLabel="JPEG, PNG, WebP"
          buttonLabel="Add attachments"
          maxSizeLabel="25 MB"
          multiple
          onFilesSelected={onAdd}
          variant="photo"
        />
        {files.length > 0 ? (
          <ul
            aria-label="Attached files"
            className="m-0 flex list-none flex-col gap-(--spacing-4) p-0"
          >
            {files.map(({ id, file }) => (
              <li
                className="flex min-w-0 items-center gap-(--spacing-8) text-text-subtle"
                key={id}
              >
                <Icon name="document" size="sm" tone="inherit" />
                <div className="min-w-0 flex-1">
                  <Text truncate>{file.name}</Text>
                </div>
                <IconButton
                  aria-label={`Remove ${file.name}`}
                  icon="x-close"
                  onClick={() => onRemove(id)}
                  size="sm"
                  type="button"
                  variant="ghost"
                />
              </li>
            ))}
          </ul>
        ) : null}
        {errors.map((error) => (
          <Text key={error} size="sm" tone="negative">
            {error}
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}
